/** Mirrors backend `TradeTypeIndicator` (MSRB RTRS Field 5). */
export type TradeTypeCode = "InterDealer" | "PurchaseFromCustomer" | "SaleToCustomer";

export const TRADE_TYPE_OPTIONS: readonly { code: TradeTypeCode; label: string }[] = [
  { code: "PurchaseFromCustomer", label: "Buy from customer" },
  { code: "SaleToCustomer", label: "Sell to customer" },
  { code: "InterDealer", label: "Interdealer" },
];

/**
 * Trade types the Markets History tab can filter and label. Interdealer and ATS
 * are not valid indicators on that tab, so they stay out of the dropdown, the
 * grid chip, and the search request.
 */
export const HISTORY_TRADE_TYPE_OPTIONS: readonly { code: TradeTypeCode; label: string }[] =
  TRADE_TYPE_OPTIONS.filter((option) => option.code !== "InterDealer");

const HISTORY_TRADE_TYPE_CODES = new Set<string>(
  HISTORY_TRADE_TYPE_OPTIONS.map((option) => option.code),
);

/** True for a trade type the History tab is allowed to filter on. */
export function isHistoryTradeType(value: string): value is TradeTypeCode {
  return HISTORY_TRADE_TYPE_CODES.has(value);
}

/** Interdealer and ATS prints carry no trade-type label on History, in any casing. */
export function isHiddenHistoryTradeIndicator(indicator: string): boolean {
  const key = indicator.trim().toLowerCase();
  return key === "interdealer" || key === "ats";
}

/** Display label for a `TradeTypeIndicator`; unknown codes pass through. */
export function tradeTypeLabel(code: string): string {
  return TRADE_TYPE_OPTIONS.find((option) => option.code === code)?.label ?? code;
}

/** Mirrors backend `BrokersBrokerIndicator` (MSRB RTRS Field 20). */
export type BrokersBrokerIndicatorCode = "Sale" | "Purchase";

/** Mirrors backend `TradeHistorySource`. */
export type TradeHistorySourceCode =
  | "MsrbRealTime"
  | "MsrbReplay"
  | "MsrbT1File"
  | "MsrbT5File"
  | "MsrbT20File";

/** Mirrors backend `YieldEnrichmentStatus`. */
export type YieldEnrichmentStatusCode =
  | "NotYetAttempted"
  | "Computed"
  | "SecurityNotFound"
  | "CalculationFailed";

/** Mirrors backend `TradeCancelledReason`. */
export type TradeCancelledReasonCode = "ExplicitCancel" | "AbsentFromLaterComprehensiveFile";

/**
 * Row shape for the Trade History grid — a subset of the full `Trade` read
 * contract (`Tower.TradeHistory.ReadModel.Trade`). Field names below mirror
 * the backend record's `Security*` properties (camelCased) since `mapTrade`
 * passes the API payload through unchanged — see `apis/tradeHistoryApi.ts`.
 * The backend record carries further SecurityMaster enrichment fields not
 * yet surfaced here (currency, ISIN, ticker, etc.); add as needed.
 */
export interface TradeRow {
  id: string;
  /** Same value as `cusip`; carried separately to satisfy `BondDataGridRow` (shared grid infra). */
  securityIdentifier: string;
  cusip: string;
  securityDescription?: string;
  tradeDate: string;
  timeOfTrade: string;
  settlementDate: string;
  tradeTypeIndicator: TradeTypeCode;
  /** Null while `isParMasked` is set: MSRB reports "MM+" instead of the par of large trades. */
  parTraded?: number;
  isParMasked: boolean;
  dollarPrice: number;
  yield: number;
  calculatedYtw?: number;
  calculatedYtm?: number;
  yieldEnrichmentStatus: YieldEnrichmentStatusCode;
  source: TradeHistorySourceCode;
  isCancelled: boolean;
  cancelledReason?: TradeCancelledReasonCode;
  /** MSRB when-issued indicator. */
  isWhenIssued?: boolean;
  /** MSRB non-transaction-based compensation indicator. */
  isNonTransactionBasedCompensation?: boolean;
  /** Null when the print is not a broker's-broker trade. */
  brokersBrokerIndicator?: BrokersBrokerIndicatorCode | null;

  // SecurityMaster enrichment fields (denormalized on the Trade read contract,
  // not a nested block like Quote/QuoteRequest's `securityEnrichment`).
  securityStateCode?: string;
  securityIssuerName?: string;
  /** Raw CGS description, kept as audit data; the Type column reads {@link securityBondType}. */
  securityTypeDescription?: string;
  /** Derived Bond Type code. */
  securityBondType?: string;
  securityFeatures?: string[];
  securityIsDoubleBarreled?: boolean;
  securityCouponRate?: number;
  securityMaturityDate?: string;
  securityNextCallDate?: string;
  securityUseOfProceeds?: string;
  securityObligorName?: string;
  securityRateStructure?: string;
  securityLatestRedemptionType?: string;
  securityTaxStatus?: string;
  securityIsBankQualified?: boolean;
  securityIsAmt?: boolean;
  securityIsTaxable?: boolean;
}
